import { Gauge, ListChecks, ShieldCheck, Warning } from '@phosphor-icons/react';
import { Card, Stack, Text } from '@fragments-sdk/ui';
import type { DashboardDrillTarget } from './dashboard-brief';
import {
  SetupAction,
  SetupStatus,
  canStepActivate,
  resolveStepActivate,
  type SetupStep,
} from './dashboard-setup-steps';
import { StatTile } from './dashboard-stat-tile';
import type { DashboardMetrics } from './dashboard-surface';
import s from './dashboard.module.scss';

// ── Operational scoreboard ───────────────────────────────────────
// Outcome KPIs shown once governance is wired into CI. Each number is the
// product of the governance loop — not raw inventory — so it appears nowhere
// else on the page.

export function Scoreboard({
  metrics,
  onNavigate,
}: {
  metrics: DashboardMetrics;
  onNavigate?: (target: DashboardDrillTarget) => void;
}) {
  const driftCaughtDelta =
    metrics.driftCaught.thisPeriod - metrics.driftCaught.priorPeriod;
  const driftShippedDelta =
    metrics.driftShipped.thisPeriod - metrics.driftShipped.priorPeriod;

  return (
    <Stack
      direction="row"
      gap="xl"
      wrap
      className={s.statGrid}
      data-tour="stat-grid"
    >
      <StatTile
        label="Drift caught"
        icon={ShieldCheck}
        value={metrics.driftCaught.thisPeriod}
        deltaValue={driftCaughtDelta}
        deltaLabel="vs prior period"
        direction="down-good"
        onClick={onNavigate ? () => onNavigate('findings') : undefined}
      />
      <StatTile
        label="Drift shipped"
        icon={Warning}
        value={metrics.driftShipped.thisPeriod}
        deltaValue={driftShippedDelta}
        deltaLabel="vs prior period"
        direction="down-good"
        onClick={onNavigate ? () => onNavigate('findings') : undefined}
      />
      <StatTile
        label="Decisions to review"
        icon={ListChecks}
        value={metrics.decisionIntelligence.total}
        deltaValue={metrics.decisionIntelligence.safeFixes}
        deltaLabel="safe fixes"
        direction="up-good"
        onClick={onNavigate ? () => onNavigate('findings') : undefined}
      />
      <StatTile
        label="Readiness"
        icon={Gauge}
        value={metrics.enrichmentScore.score}
        deltaValue={metrics.enrichmentScore.weekDelta}
        deltaLabel="this week"
        direction="up-good"
        onClick={onNavigate ? () => onNavigate('findings') : undefined}
      />
    </Stack>
  );
}

// ── Recommended action hero ──────────────────────────────────────
// A single, prominent "do this next" card. Reuses the same step model and
// a11y contract as the setup checklist rows so behaviour stays single-sourced.

export function RecommendedActionCard({
  step,
  onNavigate,
}: {
  step: SetupStep;
  onNavigate?: (target: DashboardDrillTarget) => void;
}) {
  const canActivate = canStepActivate(step, onNavigate);
  const activate = resolveStepActivate(step, onNavigate);

  return (
    <Card
      as="article"
      variant="outlined"
      padding="none"
      className={s.recommendedCard}
      data-tour={step.tourTarget}
      data-state={step.tone}
      data-interactive={canActivate || undefined}
      data-disabled={step.disabled || undefined}
      aria-label={
        canActivate ? `${step.actionLabel}: ${step.title}` : undefined
      }
      aria-disabled={step.disabled || undefined}
      onClick={canActivate && !step.disabled ? activate : undefined}
    >
      {step.illustration ? (
        <span className={s.recommendedIcon} aria-hidden="true">
          {step.illustration}
        </span>
      ) : null}
      <Stack className={s.recommendedText} gap="xs">
        <Text as="span" size="md" weight="semibold">
          {step.title}
        </Text>
        <Text as="span" size="sm" color="secondary">
          {step.description}
        </Text>
        {step.statusMessage ? (
          <SetupStatus message={step.statusMessage} tone={step.statusTone} />
        ) : null}
      </Stack>
      {!step.hideAction ? (
        <span className={s.recommendedAction}>
          <SetupAction step={step} />
        </span>
      ) : null}
    </Card>
  );
}
